/**
 * Validates an email address using the same regex as zod's `.email()` validator.
 * Use this for client-side validation before prefilling email fields to ensure the value
 * will pass server-side validation.
 *
 * This regex matches zod v3's email validation:
 * - Allows letters, digits, and common special chars before the @
 * - Requires an @ symbol
 * - Requires a domain with at least one dot
 * - Rejects leading dots, double dots, and other edge cases zod rejects
 *
 * @example
 * isValidEmail('user@example.com') // true
 * isValidEmail('user+tag@example.com') // true
 * isValidEmail('.user@example.com') // false (leading dot)
 * isValidEmail('user..name@example.com') // false (double dot)
 * isValidEmail('user@example') // false (no TLD)
 */
export const isValidEmail = (email: string): boolean => {
  // Zod v3.23's email regex from https://github.com/colinhacks/zod/blob/main/src/types.ts
  // This is more permissive than RFC 5322 but matches what the server accepts.
  // The lookaheads reject leading dots and double dots; the domain group is linear (no nested quantifiers).
  const zodEmailRegex = /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;

  return zodEmailRegex.test(email);
};
